import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SimpleTooltip } from '../ui/tooltip';
import { cn } from '../lib/utils';
import { PortalBrand } from './portal-brand';
import type { AdaptedNavGroup, AdaptedNavItem, ResolvedBranding, SidebarBadge } from './types';

interface SidebarProps {
  branding: ResolvedBranding;
  groups: AdaptedNavGroup[];
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  /** Optional per-item decorator returning a badge spec. */
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  /** Opt-in section collapse behavior. Defaults false for existing portal surfaces. */
  collapsibleGroups?: boolean;
  navGroupsInitiallyCollapsed?: boolean;
  navGroupStorageKey?: string;
  subNavigation?: boolean;
  collapsibleSidebar?: boolean;
  sidebarStorageKey?: string;
  forceDesktopCollapsed?: boolean;
  commandSlot?: ReactNode | ((options: { compact: boolean }) => ReactNode);
  workspaceSlot?: ReactNode | ((options: { compact: boolean }) => ReactNode);
  /** Rendered inside the scrolling nav, below the navigation groups. */
  extrasSlot?: ReactNode | ((options: { compact: boolean }) => ReactNode);
  /** Optional footer node. Defaults to "v0.1.0 · admin". */
  footer?: ReactNode;
}

const DEFAULT_FOOTER = (
  <>
    <span className="font-medium text-foreground">v0.1.0</span> · admin
  </>
);

export function Sidebar({
  branding,
  groups,
  mobileOpen,
  onMobileOpenChange,
  decorate,
  collapsibleGroups = false,
  navGroupsInitiallyCollapsed = false,
  navGroupStorageKey,
  subNavigation = false,
  collapsibleSidebar = false,
  sidebarStorageKey,
  forceDesktopCollapsed = false,
  commandSlot,
  workspaceSlot,
  extrasSlot,
  footer,
}: SidebarProps) {
  const { pathname } = useLocation();
  const onMobileOpenChangeRef = useRef(onMobileOpenChange);
  onMobileOpenChangeRef.current = onMobileOpenChange;
  const [activeFlyout, setActiveFlyout] = useState<string | null>(null);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    if (!collapsibleSidebar || typeof window === 'undefined' || !sidebarStorageKey) return false;
    return window.localStorage.getItem(sidebarStorageKey) === 'collapsed';
  });

  useEffect(() => {
    if (!collapsibleSidebar || typeof window === 'undefined' || !sidebarStorageKey) return;
    window.localStorage.setItem(sidebarStorageKey, desktopCollapsed ? 'collapsed' : 'expanded');
  }, [collapsibleSidebar, desktopCollapsed, sidebarStorageKey]);

  const effectiveDesktopCollapsed = forceDesktopCollapsed || desktopCollapsed;

  useEffect(() => {
    setActiveFlyout(null);
  }, [effectiveDesktopCollapsed, pathname]);

  useEffect(() => {
    onMobileOpenChangeRef.current(false);
  }, [pathname]);

  const renderNavContent = (compact: boolean, showCollapseControl: boolean) => (
    <>
      <div
        className={cn(
          'flex items-center gap-2.5 border-b border-border px-4 py-[14px]',
          compact && 'justify-center px-2',
        )}
      >
        <PortalBrand branding={branding} compact={compact} />
      </div>
      {(commandSlot || workspaceSlot) && (
        <div className={cn('space-y-2 border-b border-border p-2', compact && 'px-2')}>
          {resolveSlot(commandSlot, compact)}
          {resolveSlot(workspaceSlot, compact)}
        </div>
      )}
      <nav className="flex flex-1 flex-col gap-3 overflow-y-auto p-2 pb-[max(12px,env(safe-area-inset-bottom))]">
        {groups.map((group) => (
          <NavSection
            key={group.label}
            label={group.label}
            items={group.items}
            icon={group.icon}
            decorate={decorate}
            collapsible={collapsibleGroups || group.collapsible}
            initiallyOpen={!navGroupsInitiallyCollapsed}
            storageKey={navGroupStorageKey ? `${navGroupStorageKey}:${group.label}` : undefined}
            onNavigate={() => {
              setActiveFlyout(null);
              onMobileOpenChange(false);
            }}
            compact={compact}
            flyout={subNavigation && showCollapseControl}
            flyoutOpen={activeFlyout === group.label}
            onFlyoutOpenChange={(nextOpen) => {
              setActiveFlyout((current) =>
                nextOpen ? group.label : current === group.label ? null : current,
              );
            }}
          />
        ))}
        {resolveSlot(extrasSlot, compact)}
      </nav>
      <div
        className={cn(
          'flex items-center border-t border-border p-3 text-[11px] text-muted-foreground',
          compact ? 'justify-center' : 'justify-between gap-2',
        )}
      >
        {!compact && <div>{footer ?? DEFAULT_FOOTER}</div>}
        {collapsibleSidebar && showCollapseControl && !forceDesktopCollapsed && (
          <SimpleTooltip text={desktopCollapsed ? 'Expand navigation' : 'Collapse navigation'}>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => {
                setActiveFlyout(null);
                setDesktopCollapsed((value) => !value);
              }}
              aria-label={desktopCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              {desktopCollapsed ? (
                <ChevronRight className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronLeft className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </SimpleTooltip>
        )}
      </div>
    </>
  );

  return (
    <>
      <aside
        className={cn(
          'hidden h-app-viewport flex-shrink-0 flex-col border-r border-border bg-card transition-[width] md:flex',
          effectiveDesktopCollapsed ? 'w-[64px]' : 'w-[220px]',
        )}
      >
        {renderNavContent(effectiveDesktopCollapsed, true)}
      </aside>
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side="left"
          className="w-[88vw] max-w-[320px] border-r p-0 pt-[max(env(safe-area-inset-top),0px)] md:hidden"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Browse this portal and open your workspace controls.
          </SheetDescription>
          {renderNavContent(false, false)}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NavSection({
  label,
  items,
  icon,
  decorate,
  collapsible,
  initiallyOpen,
  storageKey,
  onNavigate,
  compact = false,
  flyout = false,
  flyoutOpen = false,
  onFlyoutOpenChange,
}: {
  label: string;
  items: AdaptedNavItem[];
  icon?: AdaptedNavGroup['icon'];
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  collapsible?: boolean;
  initiallyOpen?: boolean;
  storageKey?: string;
  onNavigate?: () => void;
  compact?: boolean;
  flyout?: boolean;
  flyoutOpen?: boolean;
  onFlyoutOpenChange?: (open: boolean) => void;
}) {
  const { pathname: sectionPathname } = useLocation();
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    if (typeof window === 'undefined' || !storageKey) return initiallyOpen ?? true;
    const stored = window.sessionStorage.getItem(storageKey);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    return initiallyOpen ?? true;
  });

  useEffect(() => {
    if (!collapsible || typeof window === 'undefined' || !storageKey) return;
    window.sessionStorage.setItem(storageKey, open ? 'open' : 'closed');
  }, [collapsible, open, storageKey]);

  if (flyout) {
    return (
      <FlyoutNavSection
        label={label}
        items={items}
        icon={icon}
        decorate={decorate}
        onNavigate={onNavigate}
        compact={compact}
        open={flyoutOpen}
        onOpenChange={onFlyoutOpenChange ?? (() => undefined)}
      />
    );
  }

  return (
    <div>
      {!label ? null : compact ? (
        <div className="sr-only">{label}</div>
      ) : collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center gap-1 px-2 pb-1.5 text-left text-[12px] font-extrabold uppercase tracking-[0.08em] text-primary transition-colors hover:text-primary/80"
          aria-expanded={open}
        >
          <ChevronDown
            className={cn('h-3 w-3 transition-transform', !open && '-rotate-90')}
            aria-hidden
          />
          <span>{label}</span>
        </button>
      ) : (
        <div className="px-2 pb-1.5 text-[12px] font-extrabold uppercase tracking-[0.08em] text-primary">
          {label}
        </div>
      )}
      <ul className={cn('flex flex-col gap-0.5', collapsible && !open && !compact && 'hidden')}>
        {items
          .filter((item) => !item.hidden)
          .map((item) => {
            const badge = decorate?.(item);
            /* A destination that owns more than one URL stays lit across all of
               them; NavLink alone only knows about `to`. */
            const claimsPath = ownsPath(item, sectionPathname);
            const link = (
              <NavLink
                onClick={onNavigate}
                to={item.to}
                aria-disabled={item.disabled}
                tabIndex={item.disabled ? -1 : undefined}
                aria-label={compact ? item.label : undefined}
                className={({ isActive: linkActive }) => {
                  const isActive = linkActive || claimsPath;
                  return cn(
                    'flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    compact && 'justify-center px-0 py-2',
                    item.disabled && 'pointer-events-none opacity-45',
                    isActive
                      ? 'bg-emphasis font-medium text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis'
                      : item.placeholder
                        ? 'text-amber-700 hover:bg-amber-50 hover:text-amber-900 dark:text-amber-300 dark:hover:bg-amber-950/30 dark:hover:text-amber-100'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  );
                }}
              >
                {({ isActive: linkActive }) => {
                  const isActive = linkActive || claimsPath;
                  return (
                  <>
                    <item.icon className="h-[15px] w-[15px] flex-shrink-0" aria-hidden />
                    {!compact && (
                      <span
                        className={cn(
                          item.placeholder &&
                            !isActive &&
                            'font-medium italic decoration-amber-700/40 underline-offset-2',
                        )}
                      >
                        {item.label}
                      </span>
                    )}
                    {typeof badge?.badge === 'number' && badge.badge > 0 && (
                      <span
                        className={cn(
                          compact
                            ? 'absolute ml-5 mt-[-18px] flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-medium tabular-nums'
                            : 'ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                          badge.badgeTone === 'destructive'
                            ? 'bg-card text-destructive'
                            : isActive
                              ? 'bg-emphasis text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis'
                              : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {badge.badge}
                      </span>
                    )}
                  </>
                  );
                }}
              </NavLink>
            );
            return (
              <li key={item.to} className="relative">
                {compact ? <SimpleTooltip text={item.label}>{link}</SimpleTooltip> : link}
                {!compact && item.children && item.children.length > 0 && (
                  <ul className="ml-4 mt-1 space-y-0.5 border-l border-border pl-2">
                    {item.children
                      .filter((child) => !child.hidden)
                      .map((child) => (
                        <li key={child.to}>
                          <NavLink
                            to={child.to}
                            onClick={onNavigate}
                            aria-disabled={child.disabled}
                            tabIndex={child.disabled ? -1 : undefined}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-2 rounded-md px-2 py-1.5 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                child.disabled && 'pointer-events-none opacity-45',
                                isActive
                                  ? 'bg-emphasis font-medium text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis'
                                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                              )
                            }
                          >
                            <child.icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span>{child.label}</span>
                          </NavLink>
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            );
          })}
      </ul>
    </div>
  );
}

function FlyoutNavSection({
  label,
  items,
  icon: SectionIcon,
  decorate,
  onNavigate,
  compact,
  open,
  onOpenChange,
}: {
  label: string;
  items: AdaptedNavItem[];
  icon?: AdaptedNavGroup['icon'];
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  onNavigate?: () => void;
  compact: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { pathname } = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const visibleItems = items.filter((item) => !item.hidden);
  const active = visibleItems.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  const badgeTotal = visibleItems.reduce(
    (total, item) => total + (decorate?.(item)?.badge ?? 0),
    0,
  );

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    onOpenChange(true);
  };
  const closeNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    onOpenChange(false);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => onOpenChange(false), 180);
  };

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  return (
    <Popover open={open} onOpenChange={(nextOpen) => (nextOpen ? show() : closeNow())}>
      <div
        onPointerEnter={show}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') scheduleClose();
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'relative flex w-full items-center gap-2 rounded-md px-2 py-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              compact && 'justify-center px-0',
              active
                ? 'bg-emphasis font-medium text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
            aria-label={compact ? label : undefined}
            aria-haspopup="menu"
            onFocus={show}
            onClick={(event) => {
              event.preventDefault();
              show();
            }}
          >
            {SectionIcon && <SectionIcon className="h-[15px] w-[15px] shrink-0" aria-hidden />}
            {!compact && <span className="truncate">{label}</span>}
            {!compact && <ChevronRight className="ml-auto h-3.5 w-3.5" aria-hidden />}
            {badgeTotal > 0 && (
              <span
                className={cn(
                  compact
                    ? 'absolute right-1 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[9px]'
                    : 'mr-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]',
                  'font-medium tabular-nums text-muted-foreground',
                )}
              >
                {badgeTotal}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={0}
          className="w-64 p-2 data-[state=open]:duration-100 data-[state=closed]:duration-75"
          onPointerEnter={show}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') scheduleClose();
          }}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <ul className="space-y-0.5" role="menu">
            {visibleItems.map((item) => {
              const badge = decorate?.(item);
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    role="menuitem"
                    aria-disabled={item.disabled}
                    tabIndex={item.disabled ? -1 : undefined}
                    onClick={() => {
                      closeNow();
                      onNavigate?.();
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-9 items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        item.disabled && 'pointer-events-none opacity-45',
                        isActive
                          ? 'bg-emphasis font-medium text-emphasis-foreground hover:bg-emphasis hover:text-emphasis-foreground focus-visible:bg-emphasis'
                          : item.placeholder
                            ? 'text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/30'
                            : 'text-foreground hover:bg-muted',
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {typeof badge?.badge === 'number' && badge.badge > 0 && (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                          badge.badgeTone === 'destructive'
                            ? 'bg-card text-destructive'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {badge.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </div>
    </Popover>
  );
}

function resolveSlot(
  slot: ReactNode | ((options: { compact: boolean }) => ReactNode) | undefined,
  compact: boolean,
) {
  return typeof slot === 'function' ? slot({ compact }) : slot;
}

/** Does this nav item claim the current path through one of its extra prefixes? */
function ownsPath(item: AdaptedNavItem, pathname: string): boolean {
  return (item.matchPrefixes ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
